import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Timeline from "../components/crm/Timeline";
import AddActivityModal from "../components/crm/AddActivityModal";
import { StatusBadge, PriorityBadge } from "../components/crm/Badges";
import { fmtCurrency, fmtDate } from "../lib/utils";
import {
  ArrowLeft, Phone, EnvelopeSimple, WhatsappLogo, Package, Users, ShoppingCart, Note,
  FloppyDisk, PencilSimple, Lock,
} from "@phosphor-icons/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { COUNTRIES, LEAD_SOURCES } from "../lib/constants";
import { toast } from "sonner";

const QUICK_ACTIONS = [
  { type: "CALL", label: "Call", Icon: Phone, color: "#3B82F6" },
  { type: "EMAIL", label: "Email", Icon: EnvelopeSimple, color: "#6366F1" },
  { type: "WHATSAPP", label: "WhatsApp", Icon: WhatsappLogo, color: "#22C55E" },
  { type: "SAMPLE", label: "Sample", Icon: Package, color: "#F43F5E" },
  { type: "MEETING", label: "Meeting", Icon: Users, color: "#8B5CF6" },
  { type: "ORDER", label: "Order", Icon: ShoppingCart, color: "#1A3A21" },
  { type: "NOTE", label: "Note", Icon: Note, color: "#6B7280" },
];

const AGENT_EDITABLE = new Set([
  "contact_name", "email", "phone", "website", "address", "city", "province", "country", "language",
  "notes", "priority", "potential_value", "product_interest",
  "preferred_contact_channel", "estimated_monthly_volume", "next_follow_up_date",
  "buyer_persona", "lead_source", "vat_number", "company_size",
  // INACTIVE-1: agents may mark their assigned lead inactive / reactivate it.
  "is_inactive",
]);

function FieldRow({ label, value, locked, children, helper }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-1">
        <label className="text-[11px] font-medium uppercase tracking-wider text-gray-500">{label}</label>
        {locked && <Lock size={11} className="text-gray-400" />}
      </div>
      {children ? children : <div className="text-sm text-gray-900">{value || "—"}</div>}
      {helper && <div className="text-[11px] text-gray-400 mt-1">{helper}</div>}
    </div>
  );
}

export default function LeadDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [lead, setLead] = useState(null);
  const [activities, setActivities] = useState([]);
  const [agents, setAgents] = useState([]);
  const [enums, setEnums] = useState({ lead_statuses: [], priorities: [], buyer_personas: [], contact_channels: [], product_interests: [] });
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState(null);
  const [editingActivity, setEditingActivity] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [editFull, setEditFull] = useState(false);

  const isAdmin = user?.role === "admin";
  const fieldDisabled = (field) => !isAdmin && !AGENT_EDITABLE.has(field);

  const load = async () => {
    setLoadError(null);
    try {
      const [l, a, u, e] = await Promise.all([
        api.get(`/leads/${id}`),
        api.get(`/leads/${id}/activities`),
        api.get("/users"),
        api.get("/meta/enums"),
      ]);
      setLead(l.data); setActivities(a.data); setAgents(u.data); setEnums(e.data);
      setForm({
        ...l.data,
        next_follow_up_date: l.data.next_follow_up_date ? l.data.next_follow_up_date.slice(0, 16) : "",
        product_interest: l.data.product_interest || [],
      });
    } catch (err) {
      const code = err?.response?.status;
      if (code === 404) setLoadError("Lead not found.");
      else if (code === 403) setLoadError("You don't have permission to view this lead.");
      else setLoadError("Unable to load lead.");
    }
  };

  useEffect(() => {
    setLead(null); setActivities([]); setLoadError(null);
    load();
    /* eslint-disable-next-line */
  }, [id]);

  const agentsMap = useMemo(() => Object.fromEntries(agents.map((a) => [a.id, a])), [agents]);

  // LEADS-OPS-2: Revenue is calculated from ORDER activities (sum of
  // order_amount). Mirrors backend revenue_service inclusion (ORDER type,
  // active activities only). Manual Potential editing has been removed.
  const revenue = useMemo(
    () => activities
      .filter((a) => a.activity_type === "ORDER" && a.is_active !== false)
      .reduce((sum, a) => sum + (parseFloat(a.order_amount) || 0), 0),
    [activities]
  );

  const openAction = (type) => { setEditingActivity(null); setModalType(type); setModalOpen(true); };
  const openEditActivity = (act) => { setEditingActivity(act); setModalType(act.activity_type); setModalOpen(true); };

  const deleteActivity = async (act) => {
    if (!window.confirm("Delete this activity?")) return;
    try {
      await api.delete(`/activities/${act.id}`);
      toast.success("Activity deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      // Only send editable + relevant fields
      const allFields = [
        "company_name", "contact_name", "email", "phone", "website", "address", "city",
        "province", "country", "language", "buyer_persona", "lead_source", "priority",
        "estimated_monthly_volume", "preferred_contact_channel", "vat_number", "company_size",
        "product_interest", "notes", "next_follow_up_date",
        // INACTIVE-1: agents may now toggle is_inactive on their assigned leads.
        "is_inactive",
        ...(isAdmin ? ["assigned_agent_id"] : []),
      ];
      const payload = {};
      allFields.forEach((f) => {
        if (!isAdmin && !AGENT_EDITABLE.has(f)) return;
        let v = form[f];
        if (f === "estimated_monthly_volume") v = parseFloat(v) || 0;
        if (f === "next_follow_up_date" && !v) v = null;
        if (f === "is_inactive") v = !!v;
        payload[f] = v;
      });
      if (isAdmin && (form.assigned_agent_id || "") === "") payload.assigned_agent_id = null;
      const { data } = await api.put(`/leads/${id}`, payload);
      setLead(data);
      setEditFull(false);
      toast.success("Saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const toggleProduct = (p) => {
    if (fieldDisabled("product_interest")) return;
    setForm((f) => {
      const arr = f.product_interest || [];
      return {
        ...f,
        product_interest: arr.includes(p) ? arr.filter((x) => x !== p) : [...arr, p],
      };
    });
  };

  if (loadError) {
    return (
      <div className="space-y-4" data-testid="lead-error">
        <button onClick={() => nav(-1)} className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-[#1A3A21]">
          <ArrowLeft size={16} /> Back
        </button>
        <div className="bg-white border border-red-200 rounded-lg p-8 text-center">
          <div className="font-display text-xl font-bold text-red-600 mb-2">Something went wrong</div>
          <div className="text-sm text-gray-600 mb-4">{loadError}</div>
          <button onClick={() => nav("/leads")} className="px-4 py-2 rounded-md bg-[#1A3A21] text-white text-sm">
            Back to leads
          </button>
        </div>
      </div>
    );
  }
  if (!lead) return <div className="text-gray-500" data-testid="lead-loading">Loading lead...</div>;

  const inputCls = "w-full border border-gray-300 rounded-md px-3 py-2 text-sm disabled:bg-gray-50 disabled:text-gray-500";

  return (
    <div className="space-y-6" data-testid="lead-detail-page">
      <button onClick={() => nav(-1)} className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-[#1A3A21]"
        data-testid="back-button">
        <ArrowLeft size={16} /> Back
      </button>

      <div className="bg-white border border-gray-200 rounded-lg p-5 md:p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-[11px] text-gray-600 bg-gray-100 px-2 py-0.5 rounded" data-testid="lead-code">
                {lead.lead_code || "—"}
              </span>
              <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight" data-testid="lead-company-name">
                {lead.company_name}
              </h1>
              <StatusBadge status={lead.lead_status} />
              <PriorityBadge priority={lead.priority} />
            </div>
            <div className="text-sm text-gray-500 mt-1">
              {lead.contact_name} {lead.city && `· ${lead.city}`} {lead.country && `· ${lead.country}`}
              {lead.buyer_persona && ` · ${lead.buyer_persona}`}
            </div>
            <div className="mt-3 flex flex-wrap gap-4 text-sm">
              {lead.email && <a href={`mailto:${lead.email}`} className="text-[#1A3A21] hover:underline">{lead.email}</a>}
              {lead.phone && <a href={`tel:${lead.phone}`} className="text-gray-700">{lead.phone}</a>}
              {lead.website && <a href={lead.website.startsWith("http") ? lead.website : `https://${lead.website}`}
                target="_blank" rel="noreferrer" className="text-gray-700 hover:underline">{lead.website}</a>}
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-wider text-gray-500">Revenue</div>
            <div className="font-display text-2xl font-bold text-[#1A3A21]" data-testid="lead-revenue">{fmtCurrency(revenue)}</div>
            <div className="text-xs text-gray-500 mt-1">
              Next follow-up: {fmtDate(lead.next_follow_up_date)}
            </div>
            <div className="text-xs text-gray-500">Last activity: {fmtDate(lead.last_activity_date)}</div>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-2">
          {QUICK_ACTIONS.map(({ type, label, Icon, color }) => (
            <button key={type} onClick={() => openAction(type)}
              data-testid={`quick-action-${type.toLowerCase()}`}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border transition-all hover:-translate-y-0.5"
              style={{ color, borderColor: `${color}40`, background: `${color}08` }}>
              <Icon size={14} weight="bold" /> {label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Timeline */}
        <div className="lg:col-span-2 bg-white border border-gray-200 rounded-lg p-5 md:p-6">
          <h3 className="font-display text-lg font-bold mb-4">Activity Timeline</h3>
          {lead?.is_inactive && (
            <div data-testid="inactive-lead-banner"
              className="mb-4 bg-yellow-50 border border-yellow-200 text-yellow-900 text-xs rounded-md px-3 py-2">
              <span className="font-semibold">Lead is inactive.</span>{" "}
              Open activities are paused and excluded from operational reminders.
            </div>
          )}
          <Timeline activities={activities} onEdit={openEditActivity} onDelete={deleteActivity} />
        </div>

        {/* Commercial Details quick-edit panel */}
        <div className="bg-white border border-gray-200 rounded-lg p-5 md:p-6 space-y-4" data-testid="lead-edit-panel">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-lg font-bold">Commercial Details</h3>
            <button onClick={() => setEditFull((s) => !s)} data-testid="toggle-full-edit"
              className="inline-flex items-center gap-1 text-xs text-[#1A3A21] font-semibold hover:underline">
              <PencilSimple size={13} /> {editFull ? "Close full edit" : "Edit all fields"}
            </button>
          </div>

          <FieldRow label="Status" locked
            helper="Status is automatically calculated from lead activities.">
            <Select value={form.lead_status} onValueChange={(v) => setForm({ ...form, lead_status: v })}
              disabled>
              <SelectTrigger data-testid="edit-status" disabled><SelectValue /></SelectTrigger>
              <SelectContent>
                {enums.lead_statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </FieldRow>

          <div data-testid="inactive-override-row">
            <label className="flex items-start gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                data-testid="lead-inactive-checkbox"
                checked={!!form.is_inactive}
                onChange={(e) => setForm({ ...form, is_inactive: e.target.checked })}
                className="mt-0.5 w-4 h-4 rounded border-gray-300 cursor-pointer"
              />
              <span className="text-sm font-medium text-gray-900">Mark lead as inactive</span>
            </label>
            <div className="text-[11px] text-gray-500 mt-1 ml-6">
              Use this only when the lead should be excluded from active dashboards and reports.
              Add a NOTE activity explaining why.
            </div>
          </div>

          <FieldRow label="Priority">
            <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}
              disabled={fieldDisabled("priority")}>
              <SelectTrigger data-testid="edit-priority"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="A">Priority A</SelectItem>
                <SelectItem value="B">Priority B</SelectItem>
                <SelectItem value="C">Priority C</SelectItem>
              </SelectContent>
            </Select>
          </FieldRow>

          <FieldRow label="Buyer Persona">
            <Select value={form.buyer_persona || "Other"} onValueChange={(v) => setForm({ ...form, buyer_persona: v })}
              disabled={fieldDisabled("buyer_persona")}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {enums.buyer_personas.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </FieldRow>

          <FieldRow label="Lead Source">
            <Select value={form.lead_source || "Other"} onValueChange={(v) => setForm({ ...form, lead_source: v })}
              disabled={fieldDisabled("lead_source")}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {LEAD_SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </FieldRow>

          <div className="grid grid-cols-2 gap-3">
            <FieldRow label="Volume (kg/mo)">
              <input type="number" className={inputCls} value={form.estimated_monthly_volume || 0}
                disabled={fieldDisabled("estimated_monthly_volume")}
                onChange={(e) => setForm({ ...form, estimated_monthly_volume: e.target.value })} />
            </FieldRow>
            <FieldRow label="Revenue" locked
              helper="Calculated automatically from ORDER activities.">
              <input
                type="text"
                className={inputCls}
                value={fmtCurrency(revenue)}
                disabled
                data-testid="lead-revenue-readonly"
              />
            </FieldRow>
          </div>

          <FieldRow label="Preferred Channel">
            <Select value={form.preferred_contact_channel || "Email"}
              onValueChange={(v) => setForm({ ...form, preferred_contact_channel: v })}
              disabled={fieldDisabled("preferred_contact_channel")}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {enums.contact_channels.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </FieldRow>

          <FieldRow label="VAT">
            <input className={inputCls} value={form.vat_number || ""}
              disabled={fieldDisabled("vat_number")}
              onChange={(e) => setForm({ ...form, vat_number: e.target.value })} />
          </FieldRow>

          <FieldRow label="Company Size">
            <input className={inputCls} value={form.company_size || ""}
              disabled={fieldDisabled("company_size")}
              onChange={(e) => setForm({ ...form, company_size: e.target.value })} />
          </FieldRow>

          <FieldRow label="Product Interest">
            <div className="flex flex-wrap gap-1.5">
              {enums.product_interests.map((p) => {
                const active = (form.product_interest || []).includes(p);
                const disabled = fieldDisabled("product_interest");
                return (
                  <button key={p} type="button" onClick={() => toggleProduct(p)} disabled={disabled}
                    className={`px-2 py-0.5 rounded-full text-[11px] font-medium border transition-colors ${
                      active ? "bg-[#1A3A21] text-white border-[#1A3A21]"
                             : "bg-white text-gray-700 border-gray-300"
                    } ${disabled ? "opacity-50 cursor-not-allowed" : "hover:border-[#1A3A21]"}`}>
                    {p}
                  </button>
                );
              })}
            </div>
          </FieldRow>

          <FieldRow label="Next Follow-up">
            <input type="datetime-local" className={inputCls} value={form.next_follow_up_date || ""}
              disabled={fieldDisabled("next_follow_up_date")}
              onChange={(e) => setForm({ ...form, next_follow_up_date: e.target.value })} />
          </FieldRow>

          <FieldRow label="Assigned Agent" locked={!isAdmin}>
            <Select value={form.assigned_agent_id || "__none"}
              onValueChange={(v) => setForm({ ...form, assigned_agent_id: v === "__none" ? "" : v })}
              disabled={!isAdmin}>
              <SelectTrigger data-testid="edit-assigned-agent"><SelectValue placeholder="Unassigned" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">Unassigned</SelectItem>
                {agents.filter((a) => a.role === "agent").map((a) => (
                  <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {lead.assigned_agent_id && (
              <div className="text-xs text-gray-500 mt-1">Currently: {agentsMap[lead.assigned_agent_id]?.name}</div>
            )}
          </FieldRow>

          <FieldRow label="Notes">
            <textarea rows={3} className={inputCls} value={form.notes || ""}
              disabled={fieldDisabled("notes")}
              onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </FieldRow>

          <button onClick={save} disabled={saving} data-testid="save-lead-detail"
            className="w-full inline-flex items-center justify-center gap-2 bg-[#1A3A21] hover:bg-[#142C19] text-white font-semibold py-2.5 rounded-md">
            <FloppyDisk size={16} /> {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>

      {/* Full Edit Lead — all fields */}
      {editFull && (
        <div className="bg-white border border-gray-200 rounded-lg p-5 md:p-6" data-testid="full-edit-form">
          <h3 className="font-display text-lg font-bold mb-4">Full Lead Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FieldRow label="Lead Code" locked>
              <input className={inputCls} value={lead.lead_code || ""} disabled />
            </FieldRow>
            <FieldRow label="Company Name" locked={!isAdmin}>
              <input className={inputCls} value={form.company_name || ""}
                disabled={!isAdmin}
                onChange={(e) => setForm({ ...form, company_name: e.target.value })} />
            </FieldRow>
            <FieldRow label="Contact Name">
              <input className={inputCls} value={form.contact_name || ""}
                disabled={fieldDisabled("contact_name")}
                onChange={(e) => setForm({ ...form, contact_name: e.target.value })} />
            </FieldRow>
            <FieldRow label="Email">
              <input className={inputCls} value={form.email || ""}
                disabled={fieldDisabled("email")}
                onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </FieldRow>
            <FieldRow label="Phone">
              <input className={inputCls} value={form.phone || ""}
                disabled={fieldDisabled("phone")}
                onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </FieldRow>
            <FieldRow label="Website">
              <input className={inputCls} value={form.website || ""}
                disabled={fieldDisabled("website")}
                onChange={(e) => setForm({ ...form, website: e.target.value })} />
            </FieldRow>
            <FieldRow label="Address">
              <input className={inputCls} value={form.address || ""}
                disabled={fieldDisabled("address")}
                onChange={(e) => setForm({ ...form, address: e.target.value })} />
            </FieldRow>
            <FieldRow label="City">
              <input className={inputCls} value={form.city || ""}
                disabled={fieldDisabled("city")}
                onChange={(e) => setForm({ ...form, city: e.target.value })} />
            </FieldRow>
            <FieldRow label="Country">
              <Select value={form.country || "__none"}
                onValueChange={(v) => setForm({ ...form, country: v === "__none" ? "" : v })}
                disabled={fieldDisabled("country")}>
                <SelectTrigger data-testid="edit-country"><SelectValue placeholder="Select country" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">— None —</SelectItem>
                  {COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </FieldRow>
            <FieldRow label="Language">
              <input className={inputCls} value={form.language || ""}
                disabled={fieldDisabled("language")}
                onChange={(e) => setForm({ ...form, language: e.target.value })} />
            </FieldRow>
            <FieldRow label="Created">
              <input className={inputCls} value={fmtDate(lead.created_at)} disabled />
            </FieldRow>
            <FieldRow label="Updated">
              <input className={inputCls} value={fmtDate(lead.updated_at)} disabled />
            </FieldRow>
          </div>
          <div className="mt-5 flex justify-end gap-2">
            <button onClick={() => setEditFull(false)} className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50">
              Cancel
            </button>
            <button onClick={save} disabled={saving}
              className="px-4 py-2 text-sm bg-[#1A3A21] hover:bg-[#142C19] text-white rounded-md font-semibold">
              {saving ? "Saving..." : "Save All"}
            </button>
          </div>
        </div>
      )}

      <AddActivityModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        leadId={id}
        defaultType={modalType}
        editing={editingActivity}
        onCreated={load}
        onDeleted={load}
      />
    </div>
  );
}
